import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, Phone, User as UserIcon, Building2, Check, Ban } from 'lucide-react';
import InquiryThread from '@/components/marketplace/InquiryThread';

const statusBadge = (s) => {
  if (s === 'new') return 'status-pending';
  if (s === 'responded') return 'status-approved';
  return 'status-needs';
};

const InquiriesInbox = () => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const paramTab = params.get('tab') === 'sent' ? 'sent' : 'received';
  const openId = params.get('open') || null;
  const [tab, setTab] = useState(paramTab);
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);

  // Keep the tab in sync with the URL so a bell click while already on the
  // inbox switches to the notified tab instead of leaving the seller on the
  // previously-open tab.
  useEffect(() => { setTab(paramTab); }, [paramTab]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get('/inquiries/received'),
        api.get('/inquiries/sent'),
      ]);
      setReceived(r.data);
      setSent(s.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep the URL's ?tab= in sync with tab selection so refresh/back keeps
  // the seller in the correct view. Preserve ?open= if present.
  const changeTab = (t) => {
    setTab(t);
    const next = new URLSearchParams(params);
    if (t === 'sent') next.set('tab', 'sent'); else next.delete('tab');
    setParams(next, { replace: true });
  };

  const markStatus = async (id, status) => {
    try {
      await api.post(`/inquiries/${id}/status?status=${status}`);
      toast.success(status === 'spam' ? 'Marked as spam — removed from your inbox' : 'Updated');
      load();
    } catch { toast.error('Update failed'); }
  };

  // Called by <InquiryThread /> when the user expands a row: server marks
  // inbound messages read and we locally clear the badge so the UI updates
  // immediately without a full reload.
  const handleThreadOpened = useCallback((inquiryId) => {
    const clear = (arr) => arr.map(i => i.id === inquiryId ? { ...i, unread_count: 0, has_unread_reply: false } : i);
    setReceived(clear);
    setSent(clear);
  }, []);

  const items = tab === 'received' ? received : sent;

  return (
    <div className='space-y-5'>
      <div>
        <h1 className='text-2xl font-semibold tracking-tight'>Inquiries Inbox</h1>
        <p className='text-muted-foreground text-sm'>Manage inquiries received on your listings and inquiries you have sent.</p>
      </div>

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList data-testid='inquiries-tabs'>
          <TabsTrigger value='received' data-testid='inquiries-tab-received'>
            Received ({received.length})
            {received.some(i => i.has_unread_reply) && (
              <span
                className='ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold'
                data-testid='inquiries-tab-received-unread-dot'
              >
                {received.reduce((n, i) => n + (i.unread_count || 0), 0)}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value='sent' data-testid='inquiries-tab-sent'>
            Sent ({sent.length})
            {sent.some(i => i.has_unread_reply) && (
              <span
                className='ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold'
                data-testid='inquiries-tab-sent-unread-dot'
              >
                {sent.reduce((n, i) => n + (i.unread_count || 0), 0)}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <Card className='p-10 text-center text-muted-foreground'>Loading…</Card>
      ) : items.length === 0 ? (
        <Card className='p-10 text-center text-muted-foreground'>No inquiries here yet.</Card>
      ) : (
        <div className='space-y-3'>
          {items.map(i => {
            const unread = i.unread_count || 0;
            const isDeepLinked = openId && openId === i.id;
            const highlight = unread > 0 || isDeepLinked;
            return (
              <Card
                key={i.id}
                className={`p-5 ${highlight ? 'border-blue-400 bg-blue-50/30' : ''}`}
                data-testid='inquiries-row'
              >
                <div className='flex items-start justify-between flex-wrap gap-3'>
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-2 flex-wrap'>
                      <span className={`inline-flex items-center rounded-full border text-[11px] px-2 py-0.5 font-medium ${statusBadge(i.status)}`}>{i.status}</span>
                      <Link to={`/listing/${i.listing_id}`} className='font-semibold hover:underline'>{i.listing_title}</Link>
                      {unread > 0 && (
                        <span
                          className='inline-flex items-center gap-1 rounded-full bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-wide'
                          data-testid={`inquiry-row-unread-${i.id}`}
                        >
                          <span className='h-1.5 w-1.5 rounded-full bg-white' aria-hidden />
                          {tab === 'received' ? `New Reply · ${unread} unread` : `Seller replied · ${unread} unread`}
                        </span>
                      )}
                    </div>
                    <div className='text-xs text-muted-foreground mt-1 flex flex-wrap gap-3'>
                      <span className='flex items-center gap-1'><UserIcon className='h-3 w-3' /> {i.inquirer_name}</span>
                      {i.inquirer_company && <span className='flex items-center gap-1'><Building2 className='h-3 w-3' /> {i.inquirer_company}</span>}
                      <span className='flex items-center gap-1'><Mail className='h-3 w-3' /> {i.inquirer_email}</span>
                      <span className='flex items-center gap-1'><Phone className='h-3 w-3' /> {i.inquirer_mobile}</span>
                    </div>
                    <p className='text-sm mt-3 whitespace-pre-line'>{i.message}</p>
                  </div>
                  {tab === 'received' && (
                    <div className='flex gap-2 shrink-0'>
                      {i.status === 'new' && <Button size='sm' variant='outline' onClick={() => markStatus(i.id, 'responded')}><Check className='h-3.5 w-3.5 mr-1' /> Mark responded</Button>}
                      {i.status !== 'closed' && <Button size='sm' variant='ghost' onClick={() => markStatus(i.id, 'closed')}>Close</Button>}
                      <Button size='sm' variant='ghost' className='text-red-600 hover:text-red-700 hover:bg-red-50' onClick={() => markStatus(i.id, 'spam')} data-testid={`inquiry-spam-${i.id}`}>
                        <Ban className='h-3.5 w-3.5 mr-1' /> Spam
                      </Button>
                    </div>
                  )}
                </div>
                {/* #15: conversation panel — either party can reply. Auto-open
                    when the inbox is deep-linked from a notification, and
                    tell the parent to clear the unread badge on expand. */}
                <InquiryThread
                  inquiryId={i.id}
                  currentUserId={user?.id}
                  defaultOpen={isDeepLinked}
                  onOpened={() => handleThreadOpened(i.id)}
                />
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InquiriesInbox;
